import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { apiUrl } from '../config/api';
import ClientLink from './ClientLink';
import { ShieldAlert, ChevronRight, ChevronDown, Search } from 'lucide-react';
import { formatDateDMY, formatDateTimeDMY } from '../utils/dateUtils';
import Pager from './UI/Pager';

const PAGE_SIZES = [10, 25, 50];

// Keys that carry the story of a change, in the order they read best. Anything
// else still shows, just after these.
const SUMMARY_KEYS = [
  'newStatus', 'status', 'amount', 'amount_paid', 'total_due', 'outstanding',
  'document_type', 'email', 'note', 'reason', 'rejection_reason',
];

const fmtWhen = (v) => {
  if (!v) return '—';
  const s = formatDateTimeDMY(v, { includeSeconds: true });
  return s === '-' ? String(v) : s;
};

// Colour an action by what it does, so a glance separates a routine read from
// something that changed money or state.
function actionTone(action = '') {
  const a = String(action).toLowerCase();
  if (/reject|cancel|terminat|written off|bad debt|delete|undone|undo/.test(a)) return 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300';
  if (/approve|paid off|activated|signed|created|added/.test(a)) return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
  if (/updated|changed|revert|reschedul|resubmit|returned/.test(a)) return 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
  if (/viewed|read/.test(a)) return 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300';
  return 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
}

function actionDot(action = '') {
  const a = String(action).toLowerCase();
  if (/reject|cancel|terminat|written off|bad debt|delete|undone|undo/.test(a)) return 'bg-rose-500';
  if (/approve|paid off|activated|signed|created|added/.test(a)) return 'bg-emerald-500';
  if (/updated|changed|revert|reschedul|resubmit|returned/.test(a)) return 'bg-amber-500';
  if (/viewed|read/.test(a)) return 'bg-slate-400';
  return 'bg-blue-500';
}

const fmtTime = (v) => {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};
const fmtDay = (v) => {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return 'Unknown date';
  return `${d.toLocaleDateString('en-GB', { weekday: 'long' })}, ${formatDateDMY(d)}`;
};
const initials = (email = '') => {
  const name = String(email || 'system').split('@')[0].replace(/[^a-zA-Z]+/g, ' ').trim();
  const parts = name.split(' ').filter(Boolean);
  return ((parts[0]?.[0] || 's') + (parts[1]?.[0] || parts[0]?.[1] || '')).toUpperCase();
};

// An update recorded as a comparison rather than a flat payload.
const hasBeforeAfter = (details) =>
  !!details &&
  typeof details === 'object' &&
  typeof details.before === 'object' &&
  typeof details.after === 'object' &&
  details.before !== null &&
  details.after !== null;

// Some events record a change as a pair of named values instead of two
// objects — a status change carries previousStatus and newStatus. Those are
// comparisons too, so they are presented as one rather than as a flat dump.
// Doing this here rather than at the point of writing means the events already
// on file read the same way, with no migration.
const VALUE_PAIRS = [
  { from: 'previousStatus', to: 'newStatus', field: 'status' },
  { from: 'oldStatus', to: 'newStatus', field: 'status' },
  { from: 'previous_status', to: 'new_status', field: 'status' },
];

// Returns { before, after, rest } when the payload describes a change, else
// null. `rest` is whatever else was recorded, kept so nothing is lost.
function asComparison(details) {
  if (!details || typeof details !== 'object') return null;
  if (hasBeforeAfter(details)) {
    const { before, after, ...rest } = details;
    return { before, after, rest };
  }
  for (const pair of VALUE_PAIRS) {
    const has = Object.prototype.hasOwnProperty.call(details, pair.from)
      && Object.prototype.hasOwnProperty.call(details, pair.to);
    if (!has) continue;
    const rest = { ...details };
    delete rest[pair.from];
    delete rest[pair.to];
    return {
      before: { [pair.field]: details[pair.from] },
      after: { [pair.field]: details[pair.to] },
      rest,
    };
  }
  return null;
}

// A one-line gist of what happened, from whichever of the details keys is there.
function summarise(row) {
  const d = row?.details;
  if (!d || typeof d !== 'object') return '—';
  const cmp = asComparison(d);
  if (cmp) {
    const changed = changedKeys(cmp.before, cmp.after);
    if (!changed.length) return 'Saved with no field changed';
    if (changed.length === 1) {
      const k = changed[0];
      return `${k}: ${fmtValue(cmp.before?.[k])} → ${fmtValue(cmp.after?.[k])}`;
    }
    return `Changed ${changed.length} fields: ${changed.slice(0, 4).join(', ')}${changed.length > 4 ? '…' : ''}`;
  }
  const present = SUMMARY_KEYS.filter((key) => !(d[key] === undefined || d[key] === null || d[key] === ''));
  if (present.length === 1 && present[0] === 'document_type') return String(d.document_type);
  const label = (key) => {
    const words = key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ').toLowerCase();
    return words.charAt(0).toUpperCase() + words.slice(1);
  };
  const parts = present.slice(0, 2).map((key) => {
    const value = typeof d[key] === 'object' ? JSON.stringify(d[key]) : String(d[key]);
    return `${label(key)}: ${value.length > 60 ? `${value.slice(0, 60)}…` : value}`;
  });
  return parts.length ? parts.join(' · ') : '';
}

// How a stored value reads on screen. null and "" are shown as themselves
// rather than as blanks, because "null became empty string" is a real change
// and collapsing both to nothing would hide it.
const fmtValue = (v) => {
  if (v === null) return 'null';
  if (v === undefined) return '—';
  if (v === '') return '""';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

// Which fields actually differ between the two sides. A key missing on one
// side counts as present-and-null, so adding a key that is null is not
// reported as a change.
function changedKeys(before, after) {
  const keys = [...new Set([...Object.keys(before || {}), ...Object.keys(after || {})])];
  return keys.filter(
    (k) => JSON.stringify(before?.[k] ?? null) !== JSON.stringify(after?.[k] ?? null)
  ).sort();
}

// One side of the comparison. Changed lines are tinted; everything else is
// muted, so the eye lands on what moved.
const DiffPanel = ({ label, obj, changed, keys, tone }) => {
  const tones = {
    before: { head: 'text-rose-700 dark:text-rose-300', line: 'bg-rose-100/70 dark:bg-rose-900/30 border-l-2 border-rose-400' },
    after: { head: 'text-emerald-700 dark:text-emerald-300', line: 'bg-emerald-100/70 dark:bg-emerald-900/30 border-l-2 border-emerald-400' },
  }[tone];

  return (
    <div className="min-w-0">
      <p className={`text-[10px] font-bold uppercase tracking-wide mb-1 ${tones.head}`}>{label}</p>
      <div className="rounded-md border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-2 overflow-x-auto max-h-64">
        <pre className="text-[11px] leading-relaxed font-mono text-gray-700 dark:text-slate-300">
          <div>{'{'}</div>
          {keys.length === 0 && <div className="pl-3 text-gray-400 dark:text-slate-500">(nothing)</div>}
          {keys.map((k) => {
            const isChanged = changed.includes(k);
            const present = obj && Object.prototype.hasOwnProperty.call(obj, k);
            return (
              <div
                key={k}
                className={`pl-2 pr-1 ${isChanged ? tones.line : 'text-gray-400 dark:text-slate-500'}`}
              >
                {'  '}"{k}": {present ? fmtValue(obj[k]) : <span className="italic">not set</span>}
              </div>
            );
          })}
          <div>{'}'}</div>
        </pre>
      </div>
    </div>
  );
};

// A recorded update, shown as before and after side by side with the changed
// fields called out.
const DetailsDiff = ({ before, after }) => {
  const [showAll, setShowAll] = useState(false);
  const changed = useMemo(() => changedKeys(before, after), [before, after]);
  const allKeys = useMemo(
    () => [...new Set([...Object.keys(before || {}), ...Object.keys(after || {})])].sort(),
    [before, after]
  );
  const keys = showAll ? allKeys : changed;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[11px] font-semibold text-gray-600 dark:text-slate-300">Changed:</span>
        {changed.length === 0 ? (
          <span className="text-[11px] text-gray-400 dark:text-slate-500">nothing — the values were saved unchanged</span>
        ) : (
          changed.map((k) => (
            <span key={k} className="px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 text-[10px] font-mono font-semibold">
              {k}
            </span>
          ))
        )}
        {allKeys.length > changed.length && (
          <button
            type="button"
            onClick={() => setShowAll((s) => !s)}
            className="ml-auto text-[11px] font-semibold text-blue-600 dark:text-blue-300 hover:underline"
          >
            {showAll ? `Show only what changed` : `Show all ${allKeys.length} fields`}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
        <DiffPanel label="Before" obj={before} changed={changed} keys={keys} tone="before" />
        <DiffPanel label="After" obj={after} changed={changed} keys={keys} tone="after" />
      </div>
    </div>
  );
};

/**
 * The audit trail for a single client or loan.
 *
 * Nothing new is recorded for this: every event already lands in audit_trail
 * against an entity, and the API gathers the ones that belong to this record —
 * including the repayments and documents that are filed against themselves
 * rather than against the loan.
 *
 * @param {'client'|'loan'} scope
 * @param {number|string}   id
 * @param {string}          title
 */
const EntityAuditTrail = ({ scope, id, title = 'Audit trail', className = '' }) => {
  const [rows, setRows] = useState([]);
  const [actions, setActions] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [q, setQ] = useState('');
  const [action, setAction] = useState('');
  // Date range (inclusive days).
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(apiUrl(`/api/audits/entity/${scope}/${id}`), {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, pageSize, q: q || undefined, action: action || undefined, from: from || undefined, to: to || undefined },
      });
      setRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
      setTotal(Number(res.data?.total || 0));
      setTotalPages(Number(res.data?.totalPages || 1));
      if (Array.isArray(res.data?.actions)) setActions(res.data.actions);
    } catch (e) {
      setError(e?.response?.data?.error || 'Could not load the audit trail.');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [scope, id, page, pageSize, q, action, from, to]);

  // Debounced, so typing in the search box does not fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  // Narrowing the list must not strand you on a page that no longer exists.
  useEffect(() => { setPage(1); }, [q, action, from, to, pageSize, scope, id]);

  const toggle = (rowId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId); else next.add(rowId);
      return next;
    });
  };

  const firstShown = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastShown = Math.min(page * pageSize, total);
  const pageSafe = Math.min(page, totalPages);

  const headerNote = useMemo(
    () => (total === 0 ? 'No activity recorded yet.' : `Showing ${firstShown}–${lastShown} of ${total}`),
    [total, firstShown, lastShown]
  );

  return (
    <section className={`rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm overflow-hidden ${className}`}>
      <header className="px-4 py-2.5 border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/60 flex items-center gap-2">
        <ShieldAlert size={15} className="text-blue-600 dark:text-blue-400" />
        <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100">{title}</h3>
        <span className="ml-auto text-xs text-gray-400 dark:text-slate-500">{headerNote}</span>
      </header>

      <div className="p-3">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search this history…"
              aria-label="Search the audit trail"
              className="w-full !h-8 !pl-8"
            />
          </div>
          <select
            value={action}
            onChange={(e) => setAction(e.target.value)}
            aria-label="Filter by action"
            className="!h-8 min-w-[150px]"
          >
            <option value="">All actions</option>
            {actions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-slate-400">
            <label htmlFor={`audit-from-${scope}-${id}`}>From</label>
            <input id={`audit-from-${scope}-${id}`} type="date" value={from} max={to || undefined}
              onChange={(e) => setFrom(e.target.value)} className="!h-8 w-36" />
            <label htmlFor={`audit-to-${scope}-${id}`}>To</label>
            <input id={`audit-to-${scope}-${id}`} type="date" value={to} min={from || undefined}
              onChange={(e) => setTo(e.target.value)} className="!h-8 w-36" />
            {(from || to) && (
              <button type="button" onClick={() => { setFrom(''); setTo(''); }}
                className="text-blue-600 dark:text-blue-300 hover:underline">Clear</button>
            )}
          </div>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            aria-label="Rows per page"
            title="Entries per page"
            className="!h-8"
          >
            {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} per page</option>)}
          </select>
          <Pager page={pageSafe} pages={totalPages} onPage={setPage} disabled={loading} className="ml-auto" />
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-8">
            <div className="w-5 h-5 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
            <span className="text-xs text-gray-500 dark:text-slate-400">Loading history…</span>
          </div>
        ) : error ? (
          <p className="py-6 text-center text-sm text-red-600 dark:text-red-400">{error}</p>
        ) : rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-400 dark:text-slate-500">
            {q || action || from || to ? 'Nothing matches that filter.' : 'No activity recorded yet.'}
          </p>
        ) : (
          <div className="space-y-4">
            {rows.reduce((days, row) => {
              const day = fmtDay(row.created_at);
              const last = days[days.length - 1];
              if (last && last.day === day) last.rows.push(row); else days.push({ day, rows: [row] });
              return days;
            }, []).map(({ day, rows: dayRows }) => (
              <div key={day}>
                <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-slate-500">{day}</p>
                <ol className="relative ml-1.5 border-l border-gray-200 dark:border-slate-700">
            {dayRows.map((row) => {
              const isOpen = expanded.has(row.id);
              const gist = summarise(row);
              return (
                <li key={row.id} className="relative pl-5 pb-3 last:pb-0">
                  <span className={`absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full ring-2 ring-white dark:ring-slate-900 ${actionDot(row.action)}`} />
                  <div className="rounded-lg border border-gray-100 dark:border-slate-700/70 bg-gray-50/60 dark:bg-slate-800/40 px-3 py-2 hover:border-gray-200 dark:hover:border-slate-600 transition-colors">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="text-[13px] font-semibold text-gray-900 dark:text-slate-100">{row.action || '—'}</span>
                      {gist && gist !== '—' && (
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${actionTone(row.action)}`}>{gist}</span>
                      )}
                      <span className="ml-auto text-[11px] text-gray-400 dark:text-slate-500 tabular-nums" title={fmtWhen(row.created_at)}>
                        {fmtTime(row.created_at)}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/40 text-[9px] font-bold text-blue-700 dark:text-blue-300">
                        {initials(row.user_email)}
                      </span>
                      <span className="text-[11px] text-gray-600 dark:text-slate-300 truncate">{row.user_email || 'system'}</span>
                      <button
                        type="button"
                        onClick={() => toggle(row.id)}
                        className="ml-auto inline-flex items-center gap-0.5 text-[11px] font-semibold text-blue-600 dark:text-blue-300 hover:underline"
                        aria-expanded={isOpen}
                      >
                        {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />} {isOpen ? 'Hide' : 'Details'}
                      </button>
                    </div>

                  {isOpen && (
                    <div className="mt-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/60 p-3 space-y-2">
                      <div className="flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-gray-600 dark:text-slate-300">
                        <span><span className="font-semibold">Record:</span> {row.entity_type === 'Client' && row.entity_id
                          ? <ClientLink clientId={row.entity_id} name={`Client ${row.entity_id}`} />
                          : <>{row.entity_type || '—'} {row.entity_id ?? ''}</>}</span>
                        <span><span className="font-semibold">IP:</span> {row.ip_address || '—'}</span>
                        <span><span className="font-semibold">User:</span> {row.user_email || 'system'}</span>
                      </div>
                      {(() => {
                        const cmp = asComparison(row.details);
                        if (cmp) {
                          const restKeys = Object.keys(cmp.rest || {});
                          return (
                            <>
                              <DetailsDiff before={cmp.before} after={cmp.after} />
                              {restKeys.length > 0 && (
                                <div className="mt-2">
                                  <p className="text-[10px] font-bold uppercase tracking-wide mb-1 text-gray-500 dark:text-slate-400">
                                    Also recorded
                                  </p>
                                  <pre className="text-[11px] leading-relaxed text-gray-700 dark:text-slate-300 bg-white dark:bg-slate-900 rounded-md border border-gray-200 dark:border-slate-700 p-2 overflow-x-auto max-h-48">
{JSON.stringify(cmp.rest, null, 2)}
                                  </pre>
                                </div>
                              )}
                            </>
                          );
                        }
                        if (row.details && typeof row.details === 'object') {
                          return (
                            <div>
                              {/* Say why there is no comparison, rather than
                                  leaving an unlabelled dump that looks broken. */}
                              <p className="text-[10px] font-bold uppercase tracking-wide mb-1 text-gray-500 dark:text-slate-400">
                                Recorded values
                                <span className="ml-2 font-normal normal-case tracking-normal text-gray-400 dark:text-slate-500">
                                  no before/after was captured for this event
                                </span>
                              </p>
                              <pre className="text-[11px] leading-relaxed text-gray-700 dark:text-slate-300 bg-white dark:bg-slate-900 rounded-md border border-gray-200 dark:border-slate-700 p-2 overflow-x-auto max-h-64">
{JSON.stringify(row.details, null, 2)}
                              </pre>
                            </div>
                          );
                        }
                        return <p className="text-[11px] text-gray-400 dark:text-slate-500">No further detail recorded.</p>;
                      })()}
                    </div>
                  )}
                  </div>
                </li>
              );
            })}
                </ol>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default EntityAuditTrail;
